import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, ROLES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, X, Pencil, CaretDown, Key, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const WILDCARD_ROLES = new Set(["admin", "ho_approver", "finance", "accounts", "director"]);
const emptyForm = {
  email: "",
  // Admin sets a per-user initial password when creating an account.
  // No default is baked in — the field is required and validated on submit.
  password: "",
  name: "",
  role: "centre_manager",
  centre_ids: [],
  all_centres: false,
  phone: "",
  is_active: true,
};

function CentreMultiSelect({ value, onChange, centres, canSelectAll, allChecked, onAllChange, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = new Set(value);
  const filtered = centres.filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(selected.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  const remove = (id) => onChange(value.filter((x) => x !== id));

  return (
    <div className="mt-2 space-y-2" data-testid={testId}>
      {canSelectAll && (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={!!allChecked}
            disabled={disabled}
            onCheckedChange={(v) => onAllChange(!!v)}
            data-testid="user-all-centres-checkbox"
          />
          <span className="uppercase tracking-widest">All Centres (wildcard, includes future)</span>
        </label>
      )}
      {!allChecked && (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className="w-full h-10 border border-zinc-300 rounded-sm px-3 flex items-center justify-between text-sm bg-white disabled:bg-zinc-50 disabled:text-zinc-400"
                data-testid="user-centres-picker"
              >
                <span className="text-zinc-500">
                  {value.length ? `${value.length} centre${value.length > 1 ? "s" : ""} selected` : "Search & select centres…"}
                </span>
                <CaretDown size={14} className="text-zinc-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 rounded-sm bg-white" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
              <Command shouldFilter={false}>
                <CommandInput placeholder="Search centres…" value={q} onValueChange={setQ} />
                <CommandList>
                  <CommandEmpty>No centres found</CommandEmpty>
                  <CommandGroup>
                    {filtered.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => toggle(c.id)}
                        data-testid={`centre-opt-${c.code}`}
                      >
                        <Checkbox checked={selected.has(c.id)} className="mr-2" />
                        <span className="text-sm">
                          {c.code} · {c.name}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {value.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {value.map((id) => {
                const c = centres.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-100 border border-zinc-300 text-xs rounded-sm"
                    data-testid={`centre-chip-${c?.code || id}`}
                  >
                    {c ? `${c.code} · ${c.name}` : id}
                    <button type="button" onClick={() => remove(id)} className="hover:text-rose-600">
                      <X size={12} weight="bold" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [centres, setCentres] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object or null
  const [f, setF] = useState(emptyForm);
  const [flt, setFlt] = useState({ role: "all", centre: "all", status: "all", q: "" });

  const load = () =>
    Promise.all([api.get("/users").then((r) => setUsers(r.data)), api.get("/centres").then((r) => setCentres(r.data))]);
  useEffect(() => {
    load();
  }, []);

  const isAdminForm = f.role === "admin";
  const canWildcard = WILDCARD_ROLES.has(f.role);

  const openCreate = () => {
    setEditing(null);
    setF(emptyForm);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setF({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      centre_ids: u.centre_ids || [],
      all_centres: !!u.all_centres,
      phone: u.phone || "",
      is_active: u.is_active !== false,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!f.name?.trim() || (!editing && !f.email?.trim())) return toast.error("Name and email are required");
    // Auto force admin → wildcard on the client too
    const payload = {
      name: f.name,
      role: f.role,
      phone: f.phone,
      centre_ids: isAdminForm ? [] : f.centre_ids,
      all_centres: isAdminForm ? true : f.all_centres,
      is_active: f.is_active,
    };
    if (f.role === "centre_manager" && !f.all_centres && f.centre_ids.length === 0) {
      return toast.error("Centre Managers must be assigned to at least one centre.");
    }
    if (!editing && (!f.password || f.password.length < 8)) {
      return toast.error("Initial password is required and must be at least 8 characters.");
    }
    try {
      if (editing) {
        if (f.password) payload.password = f.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated — access refreshed");
      } else {
        await api.post("/users", { ...payload, email: f.email, password: f.password });
        toast.success(`User created — they can log in with ${f.email}`);
      }
      setOpen(false);
      setEditing(null);
      setF(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(
      `Reset password for ${u.email}\n\nEnter a temporary password (min 8 chars). They'll be forced to change it on next login.`,
      ""
    );
    if (!pw) return;
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post(`/users/${u.id}/admin-reset-password`, {
        new_password: pw,
        force_change: true,
      });
      toast.success(`Password reset for ${u.email}. They must change it on next login.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const purgeDisabled = async () => {
    const disabled = users.filter((u) => u.is_active === false);
    if (disabled.length === 0) return toast.info("No disabled users to delete.");
    const ok = window.confirm(
      `Permanently delete ${disabled.length} disabled user${disabled.length > 1 ? "s" : ""}?\n\n` +
        disabled.map((u) => `• ${u.email}`).join("\n") +
        `\n\nThis cannot be undone. Historical records referring to them (created_by_name etc.) will remain.`
    );
    if (!ok) return;
    try {
      const r = await api.post("/admin/purge-disabled-users");
      toast.success(`Deleted ${r.data.deleted_users} user${r.data.deleted_users !== 1 ? "s" : ""}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const rows = useMemo(() => {
    return users.filter((u) => {
      if (flt.role !== "all" && u.role !== flt.role) return false;
      if (flt.status === "active" && u.is_active === false) return false;
      if (flt.status === "inactive" && u.is_active !== false) return false;
      if (flt.centre !== "all") {
        if (u.all_centres) {
          // wildcard matches any centre filter
        } else {
          const ids = u.centre_ids || [];
          if (!ids.includes(flt.centre)) return false;
        }
      }
      if (flt.q) {
        const s = flt.q.toLowerCase();
        if (!`${u.name} ${u.email}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [users, flt]);

  const centreLabel = (u) => {
    if (u.all_centres) return "All Centres";
    const ids = u.centre_ids || [];
    if (ids.length === 0) return "—";
    const names = ids.map((id) => centres.find((x) => x.id === id)?.code).filter(Boolean);
    return names.join(", ");
  };

  return (
    <div className="p-8 space-y-6" data-testid="users-root">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Access</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Users</h1>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={purgeDisabled}
            variant="outline"
            className="rounded-sm border-zinc-300 hover:border-rose-500 hover:text-rose-700 h-10 gap-2"
            data-testid="purge-disabled-btn"
          >
            <Trash size={14} weight="bold" /> Purge Disabled
          </Button>
          <Button onClick={openCreate} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2" data-testid="new-user-btn">
            <Plus size={14} weight="bold" /> New User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Input
          placeholder="Search name / email"
          value={flt.q}
          onChange={(e) => setFlt({ ...flt, q: e.target.value })}
          className="rounded-sm h-10"
          data-testid="filter-q"
        />
        <Select value={flt.role} onValueChange={(v) => setFlt({ ...flt, role: v })}>
          <SelectTrigger className="rounded-sm h-10" data-testid="filter-role">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {Object.entries(ROLES).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={flt.centre} onValueChange={(v) => setFlt({ ...flt, centre: v })}>
          <SelectTrigger className="rounded-sm h-10" data-testid="filter-centre">
            <SelectValue placeholder="Centre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Centres</SelectItem>
            {centres.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.code} · {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={flt.status} onValueChange={(v) => setFlt({ ...flt, status: v })}>
          <SelectTrigger className="rounded-sm h-10" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Assigned Centres</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.email}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4 text-zinc-600">{u.email}</td>
                <td className="p-4">{ROLES[u.role] || u.role}</td>
                <td className="p-4">
                  {u.all_centres ? (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest bg-zinc-900 text-white border border-zinc-900" data-testid={`user-centres-${u.email}`}>
                      All Centres
                    </span>
                  ) : (u.centre_ids || []).length === 0 ? (
                    <span className="text-zinc-400" data-testid={`user-centres-${u.email}`}>—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1" data-testid={`user-centres-${u.email}`}>
                      {(u.centre_ids || []).map((cid) => {
                        const c = centres.find((x) => x.id === cid);
                        return (
                          <span key={cid} className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 border border-zinc-300">
                            {c ? c.code : cid.slice(0, 6)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                      u.is_active !== false ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-600 border-zinc-300"
                    }`}
                  >
                    {u.is_active !== false ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      onClick={() => resetPassword(u)}
                      className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                      data-testid={`reset-pw-${u.email}`}
                      title="Reset password"
                    >
                      <Key size={12} /> Reset PW
                    </button>
                    <button
                      onClick={() => openEdit(u)}
                      className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                      data-testid={`edit-user-${u.email}`}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-500 text-sm">
                  No users match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit User" : "Create User"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Assign role and one or more centres. Admins automatically receive access to every centre.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Full Name</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm h-10 mt-2" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={f.email}
                disabled={!!editing}
                onChange={(e) => setF({ ...f, email: e.target.value })}
                className="rounded-sm h-10 mt-2"
                data-testid="user-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">{editing ? "Reset Password (optional)" : "Password *"}</Label>
              <Input type="password" value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                placeholder={editing ? "Leave blank to keep current password" : "min 8 characters"}
                className="rounded-sm h-10 mt-2" data-testid="user-password" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, all_centres: (v === "admin" || v === "mis") ? true : f.all_centres })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Phone</Label>
              <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">
                Assigned Centres {f.role === "centre_manager" && !f.all_centres && <span className="text-rose-600">*</span>}
              </Label>
              {isAdminForm ? (
                <div className="mt-2 h-10 border border-zinc-300 bg-zinc-50 rounded-sm px-3 flex items-center text-sm text-zinc-500" data-testid="admin-all-centres-locked">
                  Administrators automatically have access to all centres.
                </div>
              ) : (
                <CentreMultiSelect
                  value={f.centre_ids}
                  onChange={(v) => setF({ ...f, centre_ids: v })}
                  centres={centres}
                  canSelectAll={canWildcard}
                  allChecked={f.all_centres}
                  onAllChange={(v) => setF({ ...f, all_centres: v, centre_ids: v ? [] : f.centre_ids })}
                />
              )}
              {f.role === "centre_manager" && !f.all_centres && (
                <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
                  Centre Manager must have at least one centre. Single centre auto-locks the expense form.
                </p>
              )}
              {!isAdminForm && !f.all_centres && f.centre_ids.length === 0 && (
                <p className="text-[10px] text-amber-700 mt-1">
                  No centre assigned — user will NOT be able to create centre-scoped records until you assign one.
                </p>
              )}
            </div>
            {editing && (
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox
                  checked={f.is_active}
                  onCheckedChange={(v) => setF({ ...f, is_active: !!v })}
                  data-testid="user-active"
                />
                <span className="text-xs uppercase tracking-widest">Account Active</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={submit} data-testid="save-user" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
              {editing ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
